/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, {useContext, useEffect, useState} from "react";
import Stockchart from "../stockchart_plotly";
import DisplayCards from "../metricCards";
import DynamicStockCharts from "../dynamicStockCharts_plotly";
import FundaTeaser from "../fundamentals/stockFundamentalTeaser";
import Plot from "react-plotly.js";
import {fetchMarketIndices, fetchPage} from "../../api";
import MarketBreadth from "./market_breadth";
import SectorPerformance from "./sector_performance";
import LiveMarketOverview from "../marketsnap";


const IndexPerformanceChart = ({indices}) => {
    const equity_symbols =  ["NIFTY 50","BSE", "S&P500", "NASDAQ", "DAX", "FTSE",
               "HANGSENG","Nikkei225","CAC40","Shanghai_Composite"]
    const equity_indices = indices.filter(index =>
                 equity_symbols.includes(index.symbol));

    const DEFAULT_TICKERS = ["NIFTY 50","NASDAQ", "S&P500","FTSE","DAX"];

  // const [indices, setIndices] = useState([]);
  const [timeframe, setTimeframe] = useState("1Y");
  const [selectedTickers, setSelectedTickers] = useState(DEFAULT_TICKERS);


const TIMEFRAMES = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  "1Y": 365,
};
  // Function to filter data by selected timeframe
  const filterDataByTimeframe = (data) => {
    const today = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(today.getDate() - TIMEFRAMES[timeframe]);

    return data.filter((point) => new Date(point.date) >= cutoffDate);
  };

  // Function to normalize values using Min-Max Scaling
  const normalizeData = (data) => {
    const groupedData = {};

    // Group by Ticker
    data.forEach((entry) => {
      const { symbol, date, close } = entry;

      if (!groupedData[symbol]) {
        groupedData[symbol] = [];
      }

      groupedData[symbol].push({ date, close });
    });

    // Normalize each ticker's data
    Object.keys(groupedData).forEach((ticker) => {
      const prices = groupedData[ticker].map((d) => d.close);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      groupedData[ticker] = groupedData[ticker].map((d) => ({
        date: d.date,
        close: (d.close - minPrice) / (maxPrice - minPrice), // Normalize to [0,1]
      }));
    });

    return groupedData;
  };

  // Transform Data for Plotly (Filtered by Selected Tickers)
  const transformDataForPlotly = (data) => {
    const groupedData = normalizeData(data);
    return Object.keys(groupedData)
      .filter((ticker) => selectedTickers.includes(ticker)) // ✅ Only selected tickers
      .map((ticker) => ({
        x: groupedData[ticker].map((point) => point.date),
        y: groupedData[ticker].map((point) => point.close),
        type: "scatter",
        mode: "lines",
        name: ticker,
      }));
  };

  const filteredData = filterDataByTimeframe(indices);
  const plotData = transformDataForPlotly(filteredData);

  // Handle Ticker Selection Toggle
  const toggleTickerSelection = (ticker) => {
    setSelectedTickers((prev) =>
      prev.includes(ticker)
        ? prev.filter((t) => t !== ticker) // Remove if already selected
        : [...prev, ticker] // Add if not selected
    );
  };

  return (
    <div style={styles.container}>
      {/* Left Chart Section */}
      <div style={styles.chartContainer}>
        {/* Title with Timeframe Selector */}
        <div style={styles.header}>
          <h2 style={styles.title}>Market Performance</h2>
          <div style={styles.timeframeContainer}>
            {Object.keys(TIMEFRAMES).map((key) => (
              <span
                key={key}
                onClick={() => setTimeframe(key)}
                style={{
                  ...styles.timeframeOption,
                  textDecoration: timeframe === key ? "underline" : "none",
                  fontWeight: timeframe === key ? "bold" : "normal",
                }}
              >
                {key}
              </span>
            ))}
          </div>
        </div>

        {/* Performance Chart */}
        <Plot
          data={plotData}
          layout={{
            // width: 800,
            height: 800,
            // title: `Market Performance (${timeframe})`,
            xaxis: { title: "Date", type: "date" },
            yaxis: { title: "Normalized Close Price (0-1)" },
          }}
        style={{ width: '100%', height: '100%' }}  // Full width/height of container
        />
      </div>

      {/* Right Sidebar for Selecting Indices */}
      <div style={styles.sidebar}>
        {/*<h3 style={styles.sidebarTitle}>Select Indices</h3>*/}
        <ul style={styles.tickerList}>
          {Array.from(new Set(equity_indices.map((i) => i.symbol))).map((ticker) => (
            <li
              key={ticker}
              onClick={() => toggleTickerSelection(ticker)}
              style={{
                ...styles.tickerItem,
                fontWeight: selectedTickers.includes(ticker) ? "bold" : "normal",
              }}
            >
              {ticker}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ForexPerformanceChart = ({indices}) => {
    const equity_symbols =  ["USDINR","EURUSD","GBPUSD","JPYUSD"]
    const equity_indices = indices.filter(index =>
                 equity_symbols.includes(index.symbol));

    const DEFAULT_TICKERS = ["USDINR","EURUSD"];

  // const [indices, setIndices] = useState([]);
  const [timeframe, setTimeframe] = useState("1Y");
  const [selectedTickers, setSelectedTickers] = useState(DEFAULT_TICKERS);


const TIMEFRAMES = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  "1Y": 365,
};
  // Function to filter data by selected timeframe
  const filterDataByTimeframe = (data) => {
    const today = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(today.getDate() - TIMEFRAMES[timeframe]);

    return data.filter((point) => new Date(point.date) >= cutoffDate);
  };

  // Function to normalize values using Min-Max Scaling
  const normalizeData = (data) => {
    const groupedData = {};

    // Group by Ticker
    data.forEach((entry) => {
      const { symbol, date, close } = entry;

      if (!groupedData[symbol]) {
        groupedData[symbol] = [];
      }

      groupedData[symbol].push({ date, close });
    });

    // Normalize each ticker's data
    Object.keys(groupedData).forEach((ticker) => {
      const prices = groupedData[ticker].map((d) => d.close);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      groupedData[ticker] = groupedData[ticker].map((d) => ({
        date: d.date,
        close: (d.close - minPrice) / (maxPrice - minPrice), // Normalize to [0,1]
      }));
    });

    return groupedData;
  };

  // Transform Data for Plotly (Filtered by Selected Tickers)
  const transformDataForPlotly = (data) => {
    const groupedData = normalizeData(data);
    return Object.keys(groupedData)
      .filter((ticker) => selectedTickers.includes(ticker)) // ✅ Only selected tickers
      .map((ticker) => ({
        x: groupedData[ticker].map((point) => point.date),
        y: groupedData[ticker].map((point) => point.close),
        type: "scatter",
        mode: "lines",
        name: ticker,
      }));
  };

  const filteredData = filterDataByTimeframe(indices);
  const plotData = transformDataForPlotly(filteredData);

  // Handle Ticker Selection Toggle
  const toggleTickerSelection = (ticker) => {
    setSelectedTickers((prev) =>
      prev.includes(ticker)
        ? prev.filter((t) => t !== ticker) // Remove if already selected
        : [...prev, ticker] // Add if not selected
    );
  };

  return (
    <div style={styles.container}>
      {/* Left Chart Section */}
      <div style={styles.chartContainer}>
        {/* Title with Timeframe Selector */}
        <div style={styles.header}>
          <h2 style={styles.title}>Market Performance</h2>
          <div style={styles.timeframeContainer}>
            {Object.keys(TIMEFRAMES).map((key) => (
              <span
                key={key}
                onClick={() => setTimeframe(key)}
                style={{
                  ...styles.timeframeOption,
                  textDecoration: timeframe === key ? "underline" : "none",
                  fontWeight: timeframe === key ? "bold" : "normal",
                }}
              >
                {key}
              </span>
            ))}
          </div>
        </div>

        {/* Performance Chart */}
        <Plot
          data={plotData}
          layout={{
            // width: 800,
            height: 800,
            // title: `Market Performance (${timeframe})`,
            xaxis: { title: "Date", type: "date" },
            yaxis: { title: "Normalized Close Price (0-1)" },
          }}
        style={{ width: '100%', height: '100%' }}  // Full width/height of container
        />
      </div>

      {/* Right Sidebar for Selecting Indices */}
      <div style={styles.sidebar}>
        {/*<h3 style={styles.sidebarTitle}>Select Indices</h3>*/}
        <ul style={styles.tickerList}>
          {Array.from(new Set(equity_indices.map((i) => i.symbol))).map((ticker) => (
            <li
              key={ticker}
              onClick={() => toggleTickerSelection(ticker)}
              style={{
                ...styles.tickerItem,
                fontWeight: selectedTickers.includes(ticker) ? "bold" : "normal",
              }}
            >
              {ticker}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const CommodityPerformanceChart = ({indices}) => {
    const equity_symbols =  ["Gold",'Silver','WTI_Crude','Copper','Corn','Wheat']
    const equity_indices = indices.filter(index =>
                 equity_symbols.includes(index.symbol));

    const DEFAULT_TICKERS = ["Gold",'Silver','WTI_Crude'];

  // const [indices, setIndices] = useState([]);
  const [timeframe, setTimeframe] = useState("1Y");
  const [selectedTickers, setSelectedTickers] = useState(DEFAULT_TICKERS);


const TIMEFRAMES = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  "1Y": 365,
};
  // Function to filter data by selected timeframe
  const filterDataByTimeframe = (data) => {
    const today = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(today.getDate() - TIMEFRAMES[timeframe]);

    return data.filter((point) => new Date(point.date) >= cutoffDate);
  };

  // Function to normalize values using Min-Max Scaling
  const normalizeData = (data) => {
    const groupedData = {};

    // Group by Ticker
    data.forEach((entry) => {
      const { symbol, date, close } = entry;

      if (!groupedData[symbol]) {
        groupedData[symbol] = [];
      }

      groupedData[symbol].push({ date, close });
    });

    // Normalize each ticker's data
    Object.keys(groupedData).forEach((ticker) => {
      const prices = groupedData[ticker].map((d) => d.close);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      groupedData[ticker] = groupedData[ticker].map((d) => ({
        date: d.date,
        close: (d.close - minPrice) / (maxPrice - minPrice), // Normalize to [0,1]
      }));
    });

    return groupedData;
  };

  // Transform Data for Plotly (Filtered by Selected Tickers)
  const transformDataForPlotly = (data) => {
    const groupedData = normalizeData(data);
    return Object.keys(groupedData)
      .filter((ticker) => selectedTickers.includes(ticker)) // ✅ Only selected tickers
      .map((ticker) => ({
        x: groupedData[ticker].map((point) => point.date),
        y: groupedData[ticker].map((point) => point.close),
        type: "scatter",
        mode: "lines",
        name: ticker,
      }));
  };

  const filteredData = filterDataByTimeframe(indices);
  const plotData = transformDataForPlotly(filteredData);

  // Handle Ticker Selection Toggle
  const toggleTickerSelection = (ticker) => {
    setSelectedTickers((prev) =>
      prev.includes(ticker)
        ? prev.filter((t) => t !== ticker) // Remove if already selected
        : [...prev, ticker] // Add if not selected
    );
  };

  return (
    <div style={styles.container}>
      {/* Left Chart Section */}
      <div style={styles.chartContainer}>
        {/* Title with Timeframe Selector */}
        <div style={styles.header}>
          <h2 style={styles.title}>Market Performance</h2>
          <div style={styles.timeframeContainer}>
            {Object.keys(TIMEFRAMES).map((key) => (
              <span
                key={key}
                onClick={() => setTimeframe(key)}
                style={{
                  ...styles.timeframeOption,
                  textDecoration: timeframe === key ? "underline" : "none",
                  fontWeight: timeframe === key ? "bold" : "normal",
                }}
              >
                {key}
              </span>
            ))}
          </div>
        </div>

        {/* Performance Chart */}
        <Plot
          data={plotData}
          layout={{
            // width: 800,
            height: 800,
            // title: `Market Performance (${timeframe})`,
            xaxis: { title: "Date", type: "date" },
            yaxis: { title: "Normalized Close Price (0-1)" },
          }}
        style={{ width: '100%', height: '100%' }}  // Full width/height of container
        />
      </div>

      {/* Right Sidebar for Selecting Indices */}
      <div style={styles.sidebar}>
        {/*<h3 style={styles.sidebarTitle}>Select Indices</h3>*/}
        <ul style={styles.tickerList}>
          {Array.from(new Set(equity_indices.map((i) => i.symbol))).map((ticker) => (
            <li
              key={ticker}
              onClick={() => toggleTickerSelection(ticker)}
              style={{
                ...styles.tickerItem,
                fontWeight: selectedTickers.includes(ticker) ? "bold" : "normal",
              }}
            >
              {ticker}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const GlobalMarkets = ({indices}) => {
  // const stockData = useContext(StockContext);
  // const [indices, setIndices] = useState([]);
  const subSections = ["Major Indices", "Currency", "Commodities"];
  const [data, setData] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState(subSections[0]);



  console.log("SUMMARY DATA  RECVD" , indices);
  // if (!stockData) return <p>Loading stock data...</p>;

  const renderContent = () => {

    switch (activeSubTab) {
        case "Major Indices":
        return  indices.indices?.length > 0 ? (<div align={"center"}>
      {/*<IndexCards data={JSON.parse(indices.indices)} />*/}
      <IndexPerformanceChart indices={JSON.parse(indices.hist_df)}/>
            </div>) : (
                <p>Loading data...</p>
              );
      case "Currency":
        return  indices.indices?.length > 0 ? (<div align={"center"}>
      <ForexPerformanceChart indices={JSON.parse(indices.hist_df)}/>
            </div>) : (
                <p>Loading data...</p>
              );
      case "Commodities":
        return  indices.indices?.length > 0 ? (<div align={"center"}>
      <CommodityPerformanceChart indices={JSON.parse(indices.hist_df)}/>
            </div>) : (
                <p>Loading data...</p>
              );
      default:
        return <div>Select a tab to view content.</div>;
    }
  };

  return (
    <div className="section-container">
      {/* Vertical Sidebar */}
      <div className="sidebar">
        {subSections.map((tab) => (
          <button
            key={tab}
            className={`side-tab ${activeSubTab === tab ? "active" : ""}`}
            onClick={() => setActiveSubTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content Display */}
      <div className="content">
        {/*<h2>{activeSubTab}</h2>*/}
        {/*<p>Displaying data for {activeSubTab}...</p>*/}
        {renderContent()}
      </div>
    </div>
  );
};

export default GlobalMarkets;

// ✅ CSS-in-JS (Replacing Tailwind)
const styles = {
  container: {
    display: "flex",
    padding: "16px",
  },
  chartContainer: {
    width: "93%",
    paddingRight: "16px",
  },
  sidebar: {
    width: "7%",
    padding: "16px",
    borderLeft: "1px solid #ccc",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: "bold",
  },
  timeframeContainer: {
    display: "flex",
    gap: "12px",
  },
  timeframeOption: {
    cursor: "pointer",
    color: "#555",
  },
  sidebarTitle: {
    fontSize: "1rem",
    fontWeight: "bold",
    marginBottom: "8px",
  },
  tickerList: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
  tickerItem: {
    cursor: "pointer",
    color: "#555",
    padding: "4px 0",
    borderBottom: "1px dashed #ccc",
    transition: "color 0.2s",
  },
};